// src/components/ListSelector.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';

/**
 * Selected Buy List dropdown.
 *
 * Reads lists + selectedList from UserProvider and persists changes to
 * users/{uid}.selectedList, ensuring the list key exists in users/{uid}.lists.
 */
export default function ListSelector({ className = '' }) {
  const { uid, settings } = useUser();
  const { theme: th, isForest } = useTheme();
  const { t } = useTranslation();
  const [saving, setSaving] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const selected = settings?.selectedList || 'default';
  const listsObj = React.useMemo(() => settings?.lists || {}, [settings?.lists]);

  // Build option list (ensure "default" exists and appears first)
  const allOptions = React.useMemo(() => {
    const names = new Set(['default', ...Object.keys(listsObj || {})]);
    return Array.from(names).sort((a, b) => (a === 'default' ? -1 : a.localeCompare(b)));
  }, [listsObj]);

  const filtered = React.useMemo(() => {
    const s = query.trim().toLowerCase();
    if (!s) return allOptions;
    return allOptions.filter((n) => n.toLowerCase().includes(s));
  }, [allOptions, query]);

  const setSelected = async (next) => {
    if (!uid) return;
    if (next === selected) { setOpen(false); return; }
    try {
      setSaving(true);
      const userRef = doc(db, 'users', uid);
      await setDoc(
        userRef,
        {
          selectedList: next,
          lists: {
            ...listsObj,
            // Create list entry if it doesn't exist, but DON'T update lastUpdated
            // lastUpdated should ONLY be managed by price sync logic
            [next]: { ...(listsObj?.[next] || {}), listName: next },
          },
        },
        { merge: true }
      );
      setOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ListSelector] failed to update selectedList', err);
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    function onDocClick(e) {
      if (!btnRef.current) return;
      if (btnRef.current.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (!uid) return null;

  const displayName = (() => {
    const s = String(selected || 'default');
    return s.length > 18 ? s.slice(0, 17) + '…' : s;
  })();

  const buttonStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : th.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
    color: isForest ? '#e8f5e9' : th.text.primary,
  };

  const panelStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.95)' : th.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
    color: isForest ? '#e8f5e9' : th.text.primary,
  };

  const inputStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : th.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.input.border}`,
    color: isForest ? '#e8f5e9' : th.text.primary,
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        disabled={saving}
        className="max-w-[60vw] truncate rounded-xl text-sm px-3 h-9 inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60"
        style={buttonStyle}
        onClick={() => setOpen((v) => !v)}
        title={selected}
      >
        <span className="opacity-80">{t('converters.list')}:</span>
        <span className="font-medium truncate">{displayName}</span>
        <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden className="opacity-70"><path d="M5.5 7.5l4.5 5 4.5-5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
      </button>

      {open && (
        <div ref={panelRef} className="absolute right-0 z-20 mt-1 max-h-64 w-56 max-w-[90vw] overflow-hidden rounded-xl shadow-lg" style={panelStyle}>
          <div className="p-2" style={{ borderBottom: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}` }}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('manageLists.searchLists')}
              className="w-full rounded-lg px-2 py-1.5 text-sm focus:outline-none"
              style={inputStyle}
            />
          </div>
          <ul className="max-h-48 overflow-auto py-1">
            {filtered.map((name) => (
              <li
                key={name}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setSelected(name)}
                className="px-3 py-2 cursor-pointer whitespace-nowrap text-sm transition-colors"
                style={{
                  backgroundColor: name === selected ? (isForest ? 'rgba(15,58,48,0.7)' : 'rgba(0,0,0,0.05)') : 'transparent',
                }}
              >
                {name.length > 24 ? name.slice(0, 23) + '…' : name}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('converters.noMatches')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
